import { platformAuthenticatorIsAvailable, startAuthentication, startRegistration } from '@simplewebauthn/browser';

import type { ReleaseComponent, ReleaseView } from '../src/types';
import './styles.css';

const mount = document.querySelector<HTMLElement>('#app');
if (!mount) throw new Error('Release app mount is missing');
const app: HTMLElement = mount;

type Intent = 'approve' | 'reject';

function escapeHtml(value: unknown): string {
  return String(value)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');
}

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
  });
  const payload = (await response.json()) as T & { error?: { message?: string } };
  if (!response.ok) throw new Error(payload.error?.message ?? `Request failed (${response.status})`);
  return payload;
}

function shortSha(value: string): string {
  return `${value.slice(0, 8)}…${value.slice(-7)}`;
}

function verification(component: ReleaseComponent): string {
  return component.verification
    .map(
      (check) => `<li class="check check--${escapeHtml(check.status)}">
        <span class="check__status">${check.status === 'passed' ? '✓' : check.status === 'failed' ? '×' : '–'}</span>
        <span><strong>${escapeHtml(check.name)}</strong><small>${escapeHtml(check.evidence)}</small></span>
      </li>`,
    )
    .join('');
}

/**
 * Protected CICD files cannot change without the owner seeing them here first.
 * This is the human half of the rotation ceremony: the passkey signs the
 * manifest digest, and the manifest is what carries these exact blobs.
 */
function rotationNotice(component: ReleaseComponent): string {
  const rotations = component.rotations ?? [];
  if (rotations.length === 0) return '';
  return `<section class="rotation" aria-label="Protected file rotation">
    <h2>This release rewrites ${rotations.length === 1 ? 'a protected CICD file' : `${rotations.length} protected CICD files`}.</h2>
    <p>These files are the airlock's own guard rails. Approving replaces them on the box with exactly the blobs below — nothing else can change.</p>
    <ul>${rotations
      .map(
        (entry) => `<li><code>${escapeHtml(entry.path)}</code>
          <div class="revision"><span>Live <code>${escapeHtml(entry.fromBlob.slice(0, 12))}</code></span><span aria-hidden="true">→</span><span>New <code>${escapeHtml(entry.toBlob.slice(0, 12))}</code></span></div>
          <p>${escapeHtml(entry.reason)}</p></li>`,
      )
      .join('')}</ul>
  </section>`;
}

function componentCard(name: string, component: ReleaseComponent): string {
  const changed = component.mode === 'change';
  return `<section class="component" aria-label="${escapeHtml(name)} release">
    <header class="component__header">
      <div><span class="eyebrow">${escapeHtml(name)}</span><h2>${escapeHtml(component.repository)}</h2></div>
      <span class="pill ${changed ? 'pill--changed' : ''}">${changed ? 'Changes' : 'No change'}</span>
    </header>
    <div class="revision">
      <span>Current <code>${escapeHtml(component.previousCommit)}</code></span>
      <span aria-hidden="true">→</span>
      <span>Candidate <code>${escapeHtml(component.candidateCommit)}</code></span>
    </div>
    ${component.previewUrl ? `<a class="preview" href="${escapeHtml(component.previewUrl)}" target="_blank" rel="noopener noreferrer">Open staged preview <span aria-hidden="true">↗</span></a>` : '<p class="muted">No browser preview for this component.</p>'}
    <div class="facts">
      <div><h3>What changes</h3><ul>${component.changes.map((change) => `<li>${escapeHtml(change)}</li>`).join('') || '<li>Nothing in this component.</li>'}</ul></div>
      <div><h3>Checks</h3><ul class="checks">${verification(component)}</ul></div>
      <div><h3>Database impact</h3><p>${escapeHtml(component.databaseImpact)}</p></div>
      <div><h3>Infrastructure impact</h3><p>${escapeHtml(component.infrastructureImpact)}</p></div>
    </div>
  </section>`;
}

function statusCopy(status: string): string {
  const copy: Record<string, string> = {
    pending: 'Waiting for your decision', approved: 'Approved — deployment is starting', rejected: 'Rejected — production was not changed',
    expired: 'Expired — production was not changed', deploying_backend: 'Deploying the approved backend revision',
    verifying_backend: 'Verifying backend health and revision', deploying_frontend: 'Promoting the approved frontend deployment',
    verifying_frontend: 'Verifying the public frontend', verified: 'Production release verified',
    failed: 'Release stopped — a rollback needs a new Touch ID approval',
  };
  return copy[status] ?? status;
}

function remaining(view: ReleaseView): string {
  const seconds = Math.max(0, Math.ceil((Date.parse(view.release.expiresAt) - Date.parse(view.now)) / 1000));
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
}

function renderRelease(view: ReleaseView, message = ''): void {
  const { release } = view;
  const pending = release.status === 'pending';
  app.innerHTML = `<div class="shell">
    <header class="topbar"><a class="brand" href="/" aria-label="ZAPS release home"><span class="brand__mark">Z</span><span>ZAPS Release</span></a><span class="environment">PRODUCTION</span></header>
    <article class="release">
      <div class="release__intro">
        <div><span class="eyebrow">${escapeHtml(release.manifest.kind === 'rollback' ? 'Production rollback' : 'Production release')}</span>
          <h1>Review the exact release.</h1><p>Nothing becomes live until you approve this manifest with your passkey.</p></div>
        <div class="countdown ${pending ? '' : 'countdown--closed'}"><span>${pending ? 'Expires in' : 'Request closed'}</span><strong data-countdown>${pending ? remaining(view) : '—'}</strong></div>
      </div>
      <section class="summary">
        <div><span>Status</span><strong class="status status--${escapeHtml(release.status)}">${escapeHtml(statusCopy(release.status))}</strong></div>
        <div><span>Release ID</span><code>${escapeHtml(release.id)}</code></div>
        <div><span>Manifest digest</span><code>${escapeHtml(release.manifestDigest)}</code></div>
        <div><span>Requested by</span><strong>${escapeHtml(release.manifest.requestedBy)}</strong></div>
      </section>
      ${message ? `<div class="notice" role="status">${escapeHtml(message)}</div>` : ''}
      ${rotationNotice(release.manifest.backend)}
      <div class="components">${componentCard('Frontend', release.manifest.frontend)}${componentCard('Backend', release.manifest.backend)}</div>
      <section class="audit"><h2>Audit trail</h2><ol>${view.audit.map((event) => `<li><span>${escapeHtml(event.type.replaceAll('_', ' '))}</span><time>${escapeHtml(new Date(event.occurredAt).toLocaleString())}</time><code>${escapeHtml(shortSha(event.hash))}</code></li>`).join('')}</ol></section>
      <footer class="decision ${pending ? '' : 'decision--closed'}">
        <div><strong>${pending ? 'Your decision changes production.' : statusCopy(release.status)}</strong><p>${pending ? ((release.manifest.backend.rotations ?? []).length > 0 ? 'Touch ID confirms the commits, the impact, AND the protected-file rotation shown above.' : 'Touch ID confirms only the commits and impact shown above.') : 'This request cannot be reused.'}</p></div>
        ${pending ? '<div class="decision__buttons"><button class="button button--secondary" data-intent="reject">Reject</button><button class="button button--primary" data-intent="approve">Approve with Touch ID</button></div>' : ''}
      </footer>
    </article>
  </div>`;
  for (const button of document.querySelectorAll<HTMLButtonElement>('[data-intent]')) {
    button.addEventListener('click', () => void decide(release.id, button.dataset.intent as Intent));
  }
}

async function decide(releaseId: string, intent: Intent): Promise<void> {
  const buttons = [...document.querySelectorAll<HTMLButtonElement>('[data-intent]')];
  buttons.forEach((button) => { button.disabled = true; });
  try {
    const begin = await api<{ challengeId: string; options: Parameters<typeof startAuthentication>[0]['optionsJSON'] }>(
      `/api/releases/${releaseId}/decision/options`,
      { method: 'POST', body: JSON.stringify({ intent }) },
    );
    const response = await startAuthentication({ optionsJSON: begin.options });
    const view = await api<ReleaseView>(`/api/releases/${releaseId}/decision/complete`, {
      method: 'POST', body: JSON.stringify({ challengeId: begin.challengeId, intent, response }),
    });
    renderRelease(view, intent === 'approve' ? 'Approval recorded. Live status will update here.' : 'Release rejected. Production was not changed.');
    if (intent === 'approve') startPolling(releaseId);
  } catch (error) {
    const view = await api<ReleaseView>(`/api/releases/${releaseId}`);
    renderRelease(view, error instanceof Error ? error.message : 'Passkey verification did not complete.');
  }
}

function startPolling(releaseId: string): void {
  const timer = window.setInterval(async () => {
    try {
      const view = await api<ReleaseView>(`/api/releases/${releaseId}`);
      renderRelease(view);
      if (['verified', 'failed', 'rejected', 'expired'].includes(view.release.status)) clearInterval(timer);
    } catch { clearInterval(timer); }
  }, 2500);
}

interface EnrollmentStatus {
  credentialCount: number;
  setupComplete: boolean;
  canEnrollMore: boolean;
  next: 'primary' | 'backup' | 'complete';
}

const ENROLL_COPY: Record<EnrollmentStatus['next'], { eyebrow: string; title: string; blurb: string; cta: string; label: string }> = {
  primary: {
    eyebrow: 'One-time setup',
    title: 'Protect production with Touch ID.',
    blurb: 'This registers your first passkey. Your fingerprint never leaves this device.',
    cta: 'Register Touch ID passkey',
    label: 'MacBook Touch ID',
  },
  backup: {
    eyebrow: 'Add a backup',
    title: 'Register a second passkey.',
    blurb: 'Use a different device than the one already enrolled — this device gets its own key. Either passkey can then approve a production release.',
    cta: 'Register this device',
    label: 'Backup Touch ID',
  },
  complete: {
    eyebrow: 'Setup complete',
    title: 'Both passkeys are registered.',
    blurb: 'The airlock holds the maximum of two owner passkeys. Revoke one before enrolling a replacement.',
    cta: 'Register this device',
    label: 'Backup Touch ID',
  },
};

function renderSignIn(message?: string): void {
  app.innerHTML = `<div class="enroll"><section><span class="brand__mark">Z</span><span class="eyebrow">Owner verification</span><h1>Sign in to enroll a passkey.</h1><p>Verify that you are the configured production owner before registering a device.</p><a class="button button--primary" href="/api/bootstrap/github">Verify owner with GitHub</a><p id="enroll-status" class="muted" role="status">${escapeHtml(message ?? '')}</p></section></div>`;
}

async function renderEnrollment(): Promise<void> {
  let status: EnrollmentStatus;
  try {
    status = await api<EnrollmentStatus>('/api/bootstrap/enrollment');
  } catch (error) {
    renderSignIn(error instanceof Error && !/enrollment session is required/iu.test(error.message) ? error.message : undefined);
    return;
  }

  const copy = ENROLL_COPY[status.next];
  const enrolled = status.credentialCount === 1 ? '1 passkey enrolled.' : `${status.credentialCount} passkeys enrolled.`;
  const form = status.canEnrollMore
    ? `<label for="label">Passkey label</label><input id="label" value="${escapeHtml(copy.label)}" maxlength="80"><button class="button button--primary" id="enroll">${escapeHtml(copy.cta)}</button>`
    : '';

  app.innerHTML = `<div class="enroll"><section><span class="brand__mark">Z</span><span class="eyebrow">${escapeHtml(copy.eyebrow)}</span><h1>${escapeHtml(copy.title)}</h1><p>${escapeHtml(copy.blurb)}</p><p class="muted">${escapeHtml(enrolled)}</p>${form}<p id="enroll-status" class="muted" role="status"></p></section></div>`;

  document.querySelector<HTMLButtonElement>('#enroll')?.addEventListener('click', async () => {
    const line = document.querySelector<HTMLElement>('#enroll-status');
    const button = document.querySelector<HTMLButtonElement>('#enroll');
    const label = document.querySelector<HTMLInputElement>('#label')?.value ?? '';
    if (button) button.disabled = true;
    try {
      if (!(await platformAuthenticatorIsAvailable())) {
        throw new Error('This browser cannot access the platform authenticator. Open this page in Brave or Safari on your Mac.');
      }
      if (line) line.textContent = 'Waiting for Touch ID or your device password…';
      const begin = await api<{ registrationId: string; options: Parameters<typeof startRegistration>[0]['optionsJSON'] }>('/api/bootstrap/registration/options', {
        method: 'POST', body: JSON.stringify({ label }),
      });
      const response = await startRegistration({ optionsJSON: begin.options });
      const result = await api<EnrollmentStatus & { credentialId: string }>('/api/bootstrap/registration/complete', { method: 'POST', body: JSON.stringify({ registrationId: begin.registrationId, response }) });
      if (line) {
        line.textContent = result.canEnrollMore
          ? 'Passkey registered. You can enroll one more device from a fresh GitHub sign-in.'
          : 'Passkey registered. Both owner passkeys are now enrolled.';
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Enrollment did not complete.';
      if (line) {
        line.textContent = /excludeCredentials|already registered|InvalidStateError/iu.test(message)
          ? 'This device already holds an enrolled passkey. Use a different device for the backup.'
          : message;
      }
      if (button) button.disabled = false;
    }
  });
}

async function boot(): Promise<void> {
  const match = location.pathname.match(/^\/r\/([0-9a-f-]{36})$/u);
  if (match?.[1]) {
    try { const view = await api<ReleaseView>(`/api/releases/${match[1]}`); renderRelease(view); if (!['pending', 'verified', 'failed', 'rejected', 'expired'].includes(view.release.status)) startPolling(match[1]); }
    catch (error) { app.innerHTML = `<div class="fatal"><h1>Release unavailable</h1><p>${escapeHtml(error instanceof Error ? error.message : 'The release request could not be loaded.')}</p></div>`; }
    return;
  }
  if (location.pathname === '/enroll') { await renderEnrollment(); return; }
  app.innerHTML = `<div class="fatal"><span class="brand__mark">Z</span><h1>Production Release Airlock</h1><p>Open an exact release link to review a production change.</p></div>`;
}

void boot();
